import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { StatusBadge } from '../components/StatusBadge';
import { Activity, Server, RefreshCw } from 'lucide-react';

interface ServiceStatus {
  name: string;
  status: 'online' | 'offline' | 'degraded';
  responseTimeMs?: number;
}

interface SystemInfo {
  nodeVersion: string;
  platform: string;
  arch: string;
  uptimeFormatted: string;
  memory: { rss: number; heapUsed: number; heapTotal: number };
  checkedAt: string;
}

export function MonitoringPage() {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [overall, setOverall] = useState('');
  const [system, setSystem] = useState<SystemInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [svc, sys] = await Promise.all([
        api.get<{ overall: string; services: ServiceStatus[] }>('/monitoring/services'),
        api.get<SystemInfo>('/monitoring/system'),
      ]);
      setServices(svc.services);
      setOverall(svc.overall);
      setSystem(sys);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Monitoring</h1>
          <p className="text-sm text-slate-500 mt-1">Service-Status und System-Info</p>
        </div>
        <button onClick={load} className="btn-secondary text-sm" disabled={loading}>
          <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32 text-slate-500">Laden ...</div>
      ) : (
        <>
          {/* Overall */}
          <div className={`card flex items-center gap-4 ${
            overall === 'healthy' ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'
          }`}>
            <Activity size={24} className={overall === 'healthy' ? 'text-emerald-600' : 'text-amber-600'} />
            <div>
              <div className="font-semibold text-slate-900">
                {overall === 'healthy' ? 'Alle Services online' : 'Einige Services beeinträchtigt'}
              </div>
              <div className="text-sm text-slate-500">
                {services.filter((s) => s.status === 'online').length} von {services.length} Services online
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="card">
            <h2 className="font-semibold text-slate-900 mb-4">Services</h2>
            <div className="space-y-3">
              {services.map((s) => (
                <div key={s.name} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                  <span className="font-medium text-slate-700">{s.name}</span>
                  <div className="flex items-center gap-4">
                    {s.responseTimeMs !== undefined && (
                      <span className={`text-sm font-mono ${
                        s.responseTimeMs < 200 ? 'text-emerald-600' :
                        s.responseTimeMs < 1000 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {s.responseTimeMs}ms
                      </span>
                    )}
                    <StatusBadge status={s.status} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* System-Info */}
          {system && (
            <div className="card">
              <div className="flex items-center gap-2 mb-4">
                <Server size={18} className="text-slate-500" />
                <h2 className="font-semibold text-slate-900">Dashboard-System</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Uptime</p>
                  <p className="font-semibold text-slate-900">{system.uptimeFormatted}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Node.js</p>
                  <p className="font-semibold text-slate-900">{system.nodeVersion}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">RAM (Heap)</p>
                  <p className="font-semibold text-slate-900">{system.memory.heapUsed} / {system.memory.heapTotal} MB</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">RSS</p>
                  <p className="font-semibold text-slate-900">{system.memory.rss} MB</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
